import { useDocumentTitle } from '../app/useDocumentTitle';
import { USER_COLORS } from '@miroom/shared';
import { ArrowLeft, Monitor, Moon, Sun } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../application/ports/api';
import { usePrefs } from '../../application/stores/prefsStore';
import { toast } from '../../application/stores/toastStore';
import { useAuth } from '../app/useAuth';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
import { Modal } from '../components/Modal';
import { Segmented } from '../components/Segmented';
import { TextField } from '../components/TextField';
import { errorMessage, useT } from '../i18n/i18n';
import styles from './SettingsPage.module.css';

export function SettingsPage() {
  const t = useT();
  useDocumentTitle(t('settings.title'));
  const { user, updateProfile } = useAuth();
  const prefs = usePrefs();
  const [name, setName] = useState(user?.name ?? '');
  const [color, setColor] = useState(user?.color ?? USER_COLORS[0]);
  const [saving, setSaving] = useState(false);
  if (!user) return null;

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), color });
      toast.success(t('settings.saved'));
    } catch (err) {
      toast.error(errorMessage(t, err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/boards" className={styles.back}>
          <ArrowLeft size={18} /> {t('settings.back')}
        </Link>
        <Logo />
      </header>
      <main className={styles.main}>
        <h1>{t('settings.title')}</h1>

        <section className={styles.card} aria-labelledby="profile-h">
          <h2 id="profile-h">{t('settings.profile')}</h2>
          <form className={styles.form} onSubmit={saveProfile}>
            <div className={styles.avatarRow}>
              <Avatar name={name || user.name} color={color} size={56} />
              <div>
                <p className={styles.label}>{t('settings.color')}</p>
                <div className={styles.colors} role="radiogroup" aria-label={t('settings.color')}>
                  {USER_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      role="radio"
                      aria-checked={c === color}
                      aria-label={c}
                      className={styles.color}
                      style={{ background: c }}
                      onClick={() => setColor(c)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <TextField
              label={t('settings.name')}
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              error={name.trim() ? undefined : t('auth.nameRequired')}
            />
            <TextField label={t('settings.email')} value={user.email} disabled readOnly />
            <div>
              <Button
                type="submit"
                loading={saving}
                disabled={!name.trim() || (name.trim() === user.name && color === user.color)}
              >
                {t('settings.save')}
              </Button>
            </div>
          </form>
        </section>

        <section className={styles.card} aria-labelledby="appearance-h">
          <h2 id="appearance-h">{t('settings.appearance')}</h2>
          <div className={styles.prefs}>
            <div>
              <p className={styles.label}>{t('theme.label')}</p>
              <Segmented
                label={t('theme.label')}
                value={prefs.theme}
                onChange={prefs.setTheme}
                options={[
                  { value: 'system', label: t('theme.system'), icon: <Monitor size={14} /> },
                  { value: 'light', label: t('theme.light'), icon: <Sun size={14} /> },
                  { value: 'dark', label: t('theme.dark'), icon: <Moon size={14} /> },
                ]}
              />
            </div>
            <div>
              <p className={styles.label}>{t('locale.label')}</p>
              <Segmented
                label={t('locale.label')}
                value={prefs.locale}
                onChange={prefs.setLocale}
                options={[
                  { value: 'ru', label: t('locale.ru') },
                  { value: 'en', label: t('locale.en') },
                ]}
              />
            </div>
          </div>
        </section>

        <SessionsSection />
        <DangerSection />
      </main>
    </div>
  );
}

function SessionsSection() {
  const t = useT();
  const navigate = useNavigate();
  const { logoutAll } = useAuth();
  const [busy, setBusy] = useState(false);
  const signOut = async () => {
    setBusy(true);
    try {
      await logoutAll();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(errorMessage(t, err));
      setBusy(false);
    }
  };
  return (
    <section className={styles.card} aria-labelledby="sessions-h">
      <h2 id="sessions-h">{t('settings.sessions')}</h2>
      <p className={styles.muted}>{t('settings.sessionsText')}</p>
      <div>
        <Button variant="secondary" loading={busy} onClick={signOut}>
          {t('settings.logoutAll')}
        </Button>
      </div>
    </section>
  );
}

function DangerSection() {
  const t = useT();
  const navigate = useNavigate();
  const { user, deleteAccount } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await deleteAccount(email.trim().toLowerCase());
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.fields.email === 'mismatch'
          ? t('settings.emailMismatch')
          : errorMessage(t, err),
      );
      setBusy(false);
    }
  };
  return (
    <section className={`${styles.card} ${styles.danger}`} aria-labelledby="danger-h">
      <h2 id="danger-h">{t('settings.danger')}</h2>
      <p className={styles.muted}>{t('settings.dangerText')}</p>
      <div>
        <Button variant="danger" onClick={() => setOpen(true)}>
          {t('settings.deleteAccount')}
        </Button>
      </div>
      {open && (
        <Modal title={t('settings.deleteConfirmTitle')} onClose={() => setOpen(false)}>
          <form className={styles.form} onSubmit={submit}>
            <p>{t('settings.deleteConfirmText')}</p>
            <TextField
              label={t('auth.email')}
              type="email"
              autoComplete="off"
              placeholder={user?.email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={error}
              data-autofocus
            />
            <Button
              type="submit"
              variant="danger"
              loading={busy}
              disabled={email.trim().toLowerCase() !== user?.email}
            >
              {t('settings.deleteAccount')}
            </Button>
          </form>
        </Modal>
      )}
    </section>
  );
}
